import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const customerOnly = searchParams.get('role') === 'customer';
  
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formData, setFormData] = useState({
    identifier: '',  // Can be email or username for login
    email: '',
    username: '',
    password: '',
    name: '',
    phone: '',
    role: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  // If customer-only mode, auto-switch to register and set role
  useEffect(() => {
    if (customerOnly) {
      setIsRegister(true);
      setFormData(prev => ({ ...prev, role: 'customer' }));
    }
  }, [customerOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Validate at least email or username is provided
        if (!formData.email && !formData.username) {
          toast.error('Please provide either an email or username');
          setLoading(false);
          return;
        }
        
        await axios.post(`${API}/auth/register`, {
          email: formData.email || undefined,
          username: formData.username || undefined,
          password: formData.password,
          name: formData.name,
          phone: formData.phone,
          role: formData.role,
          address: formData.address || undefined
        });
        toast.success('Registration successful! Please wait for admin approval.');
        setIsRegister(false);
        setFormData({ identifier: '', email: '', username: '', password: '', name: '', phone: '', role: '', address: '' });
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          identifier: formData.identifier,
          password: formData.password
        });
        
        // Check if password reset is required
        if (response.data.user.password_reset_required) {
          setTempToken(response.data.access_token);
          setTempUser(response.data.user);
          setShowChangePassword(true);
          toast.info('Please set a new password');
        } else {
          toast.success('Login successful!');
          onLogin(response.data.access_token, response.data.user);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      onLogin(tempToken, { ...tempUser, password_reset_required: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/1fdjtarl_Green%20Label%20with%20no%20Logo.jpeg" 
              alt="Wilyrash Farm Logo" 
              className="w-32 h-32 mx-auto mb-6 rounded-full object-cover"
            />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Wilyrash Farm
            </h1>
            <p className="text-lg text-slate-600 mt-2">
              {customerOnly ? 'Customer Registration' : (isRegister ? 'Create your account' : 'Welcome back')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  data-testid="register-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                  required
                />
              </div>
            )}

            {isRegister ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email <span className="text-slate-400">(or Username below)</span>
                  </label>
                  <input
                    type="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Username <span className="text-slate-400">(or Email above)</span>
                  </label>
                  <input
                    type="text"
                    data-testid="username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                    placeholder="Choose a username"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email or Username
                </label>
                <input
                  type="text"
                  data-testid="identifier-input"
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                  placeholder="Enter your email or username"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isRegister && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Mobile Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    data-testid="phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                    placeholder="Enter your mobile number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Register As <span className="text-red-500">*</span>
                  </label>
                  {customerOnly ? (
                    <div className="w-full h-10 rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-700">
                      Customer
                    </div>
                  ) : (
                    <select
                      data-testid="role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                      className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                    >
                      <option value="" disabled>-- Select Role --</option>
                      <option value="staff">Staff</option>
                      <option value="support_staff">Support Staff</option>
                      <option value="distributor">Distributor</option>
                      <option value="customer">Customer</option>
                      <option value="vet_doctor">Vet Doctor</option>
                    </select>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.role === '' && 'Please select your role to continue'}
                    {formData.role === 'staff' && 'Staff can enter production and sales data'}
                    {formData.role === 'support_staff' && 'Support Staff assist with farm operations and maintenance'}
                    {formData.role === 'distributor' && 'Distributors can access orders, payments and empty feed bags'}
                    {formData.role === 'customer' && 'Customers can place order requests'}
                    {formData.role === 'vet_doctor' && 'Vet Doctors can submit visitation reports'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Address (Optional)
                  </label>
                  <input
                    type="text"
                    data-testid="address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
                    placeholder="Your address"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-medium py-2.5 rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {!isRegister && !customerOnly && (
            <div className="mt-4 text-center">
              <button
                data-testid="forgot-password-button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-1"
              >
                <HelpCircle className="w-4 h-4" />
                Forgot Password?
              </button>
            </div>
          )}

          {/* Self-registration disabled - Only admin can create new users */}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-black/50 z-10" />
        <img
          src="https://images.unsplash.com/photo-1758395522044-c55fd32e778a?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Poultry Farm"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center text-white p-12">
          <div>
            <h2 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Manage Your Farm
            </h2>
            <p className="text-xl text-slate-200">
              Track production, manage sales, and grow your poultry business
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Forgot Password?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                <strong>To reset your password:</strong>
              </p>
              <ol className="mt-2 text-amber-700 text-sm list-decimal list-inside space-y-1">
                <li>Contact the system administrator</li>
                <li>Request a password reset</li>
                <li>The admin will provide you with a one-time password</li>
                <li>Login with the one-time password</li>
                <li>You will be prompted to create a new password</li>
              </ol>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-slate-700 text-sm">
                <strong>Contact Admin:</strong><br />
                Email: admin@wilyrash.com
              </p>
            </div>
            <Button 
              onClick={() => setShowForgotPassword(false)} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog (after admin reset) */}
      <Dialog open={showChangePassword} onOpenChange={() => {}}>
        <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set New Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-700 text-sm">
                Your password was reset by an administrator. Please create a new password to continue.
              </p>
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="change-password-submit"
            >
              Set New Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
